import { css } from '@emotion/react';
import {
  faCircleInfo,
  faCircleXmark,
  faExclamationTriangle, faListTree,
} from '@fortawesome/pro-solid-svg-icons';
import type { OverlayScrollbarsComponentRef } from 'overlayscrollbars-react';
import {
  type FC, useCallback, useMemo, useRef,
} from 'react';
import {
  RoundButtonComponent, RoundButtonSize,
} from '~/_shared/baseComponents/buttons/roundButton/roundButton.component';
import { RoundButtonStyle } from '~/_shared/baseComponents/buttons/roundButton/roundButton.styles';
import {
  getRegularDropdownTriggerSize, RegularDropdownComponent,
} from '~/_shared/baseComponents/dropdown';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { InputSize } from '~/_shared/baseComponents/inputs';
import { HintTooltipComponent } from '~/_shared/baseComponents/tooltip/hintTooltip.component';
import { AddMorePlaceholderComponent } from '~/_shared/components/addMorePlaceholder/addMorePlaceholder.component';
import { hexColorWithOpacityToString } from '~/_shared/components/colorPicker/colorPicker.helpers';
import { LimitNumberOfLinesComponent } from '~/_shared/components/limitNumberOfLines/limitNumberOfLines.component';
import { OverlayScrollbarComponent } from '~/_shared/components/scrollbar/overlayScrollbar.component';
import { ScrollbarType } from '~/_shared/components/scrollbar/scrollbar.component';
import {
  getScrollableElementFromScrollbarRef, scrollToBottom,
} from '~/_shared/components/scrollbar/utils';
import type { Theme } from '~/_shared/themes/theme.model';
import { type MapInfo } from '~/_shared/types/map';
import { type PerSpreadsheetMatchupData } from '~/_shared/types/spreadsheetData/matchupData';
import { delay } from '~/_shared/utils/delay';
import { useTranslation } from '~/_shared/utils/hooks';
import { notNull } from '~/_shared/utils/typeGuards';
import { type IntersectsMapItem } from '~/map/layered/createLayeredMapModal/MatchupColumnsSection/matchupColumnsSection.helpers';
import { type MapIntersect } from '~/map/map.repository';
import { Trans } from '~/translations/Trans';
import { LayeredMapSectionHolderComponent } from '../../layeredMapSectionHolderComponent';
import { SectionAutocompleteComponent } from './SectionAutocomplete/sectionAutocomplete.component';
import { useMatchupColumnsSection } from './useMatchupColumnsSection';

const DROPDOWN_TRIGGER_SIZE = InputSize.Medium;
const MAIN_COLUMN_WIDTH = 223;
const SECONDARY_COLUMN_WIDTH = 183;
const MAP_NAME_BOTTOM_MARGIN = 8;
const SCROLLBAR_MARGIN = 2;
const UNIFIED_MARGIN = 8;
const MAX_VERTICAL_HEIGHT = 250;
const MAP_NAME_HEIGHT = 32;
const COLUMN_HEIGHT = getRegularDropdownTriggerSize(DROPDOWN_TRIGGER_SIZE);
const MAX_ITEMS_IN_COLUMN = Math.floor(MAX_VERTICAL_HEIGHT / ((COLUMN_HEIGHT + UNIFIED_MARGIN) - UNIFIED_MARGIN));
const SCROLL_DELAY = 50;
const REMOVE_ICON_FONT_SIZE = 18;

const helpfulTipComponents = {
  p: <p />,
  faCircleXmark: <FontAwesomeIcon icon={faCircleXmark} />,
  faListTree: <FontAwesomeIcon icon={faListTree} />,
};

const unMatchedDropDownTriggerStyle = (theme: Theme) => css({
  color: theme.textColors.disabled,
});

const containerStyle = css({
  display: 'flex',
});

const helpfulTipStyle = css({
  display: 'flex',
  gap: 16,
  alignItems: 'center',
  paddingBottom: 8,
});

const baseMapWrapperStyle = (theme: Theme) => css({
  background: hexColorWithOpacityToString(theme.borderColors.primary, .5),
  border: `1px solid ${theme.borderColors.primary}`,
  padding: '0 8px',
  marginTop: 8,
  borderRadius: 4,
});

const baseMapsScrollbarStyle = css({
  display: 'inline-flex',
  flex: 1,
  flexDirection: 'column',
  minWidth: SECONDARY_COLUMN_WIDTH,
  paddingBottom: UNIFIED_MARGIN,
});

const horizontalScrollbarProperties = {
  display: 'inline-table',
  paddingBottom: UNIFIED_MARGIN,
  width: '100%',
};

const baseMapsWrapperStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: UNIFIED_MARGIN,
});

const baseMapColumnStyle = css({
  width: SECONDARY_COLUMN_WIDTH,
});

const baseMapsNamesStyle = css({
  display: 'flex',
  alignItems: 'center',
  fontWeight: 'bold',
  gap: 8,
  height: MAP_NAME_HEIGHT,
});

const ellipsisStyle = css(baseMapColumnStyle, {
  boxSizing: 'border-box',
  overflow: 'hidden',
  padding: `0 ${UNIFIED_MARGIN}px`,
  textAlign: 'center',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const rowStyle = css({
  alignItems: 'center',
  display: 'flex',
  gap: 8,
});

const combinedColumnNameStyle = css({
  alignItems: 'end',
  display: 'flex',
  fontWeight: 'bold',
  justifyContent: 'center',
  marginBottom: MAP_NAME_BOTTOM_MARGIN,
  minHeight: `${MAP_NAME_HEIGHT}px`,
  padding: `0 ${UNIFIED_MARGIN}px 0 35px`,
  textAlign: 'center',
});

const divisionStyle = css({
  paddingLeft: UNIFIED_MARGIN,
  marginLeft: SCROLLBAR_MARGIN,
});

const combinedSectionStyle = css({
  height: MAX_VERTICAL_HEIGHT + COLUMN_HEIGHT + 2 * UNIFIED_MARGIN + MAP_NAME_HEIGHT,
  minWidth: MAIN_COLUMN_WIDTH,
  position: 'relative',
  width: MAIN_COLUMN_WIDTH,
});

const combinedColumnsStyle = ({ isPadded }: { isPadded: boolean }) => css(
  divisionStyle, {
    display: 'flex',
    flexDirection: 'column',
    gap: UNIFIED_MARGIN,
    paddingRight: isPadded ? UNIFIED_MARGIN : 0,
  });

const combinedDropdownStyle = css({
  boxSizing: 'border-box',
  flex: 1,
  width: `calc(100% - ${REMOVE_ICON_FONT_SIZE}px - ${UNIFIED_MARGIN}px)`,
  position: 'relative',
});

const addMoreWrapperStyle = css({
  marginLeft: SCROLLBAR_MARGIN,
  padding: `${UNIFIED_MARGIN}px 0 0 ${UNIFIED_MARGIN}px`,
});

const warningButtonStyle = css({
  fontSize: 16,
  fontWeight: 400,
  border: 'none',
  background: 'none',
  padding: 0,
  margin: 0,
  display: 'flex',
  gap: 4,
  alignItems: 'center',
});

const unnamedColumnsButtonStyle = (theme: Theme) => css({
  color: theme.textColors.danger,
  '&:hover': {
    color: theme.textColors.dangerOnHover,
  },
});

const newColumnsButtonStyle = (theme: Theme) => css({
  color: theme.textColors.info,
  '&:hover': {
    color: theme.textColors.linkOnHover,
  },
});

const warningButtonTextStyle = css({
  textDecoration: 'underline',
});

const warningColumnsButtonIconStyle = css({
  fontSize: 18,
  textDecoration: 'underline',
});

type MatchupColumnsSectionComponentProps = Readonly<{
  columnData?: PerSpreadsheetMatchupData;
  disableColumnNamesEditing?: boolean;
  intersects: MapIntersect[];
  isLoading: boolean;
  layeringMapInfo?: MapInfo | null;
  mapName: string;
  maps: IntersectsMapItem[];

  getMapFromRealSpreadsheetId: (mapList: IntersectsMapItem[], spreadsheetId: number) => IntersectsMapItem | null;
  getMapIdFromPrimarySpreadsheetId: (mapList: IntersectsMapItem[], spreadsheetId: number) => number | null;
  getPrimarySpreadsheetIdFromMapId: (mapList: IntersectsMapItem[], mapId: number) => number | null;
  getRealSpreadsheetIdFromMapId: (mapList: IntersectsMapItem[], mapId: number) => number | null;
  getSpreadsheetIdFromMapId: (mapList: IntersectsMapItem[], mapId: number) => number | null;

  onSubmit: (data: MapIntersect[]) => void;
}>;

export const MatchupColumnsSectionComponent: FC<MatchupColumnsSectionComponentProps> = (props) => {
  const { disableColumnNamesEditing, maps, isLoading } = props;
  const columnNamesScrollbarRef = useRef<OverlayScrollbarsComponentRef>(null);
  const baseScrollbarRef = useRef<OverlayScrollbarsComponentRef>(null);
  const columnNamesContainerRef = useRef<HTMLDivElement>(null);
  const [t] = useTranslation();

  const {
    onAddRow,
    onRemoveRow,
    onTargetColumnNameChange,
    rows,
    onSourceColumnChange,
    sourceColumnsPerMap,
    handleGenerateIntersectsAndSubmit,
    areAllRowsSelected,
    toggleRowInclusion,
    sourceColumnComparator,
    firstEmptyRowIndex,
    firstNewRowIndex,
  } = useMatchupColumnsSection({
    maps,
    columnData: props.columnData,
    getMapIdFromPrimarySpreadsheetId: props.getMapIdFromPrimarySpreadsheetId,
    getMapFromRealSpreadsheetId: props.getMapFromRealSpreadsheetId,
    getSpreadsheetIdFromMapId: props.getSpreadsheetIdFromMapId,
    getRealSpreadsheetIdFromMapId: props.getRealSpreadsheetIdFromMapId,
    getPrimarySpreadsheetIdFromMapId: props.getPrimarySpreadsheetIdFromMapId,
    intersects: props.intersects,
    isLoading,
    onSubmit: props.onSubmit,
    layeringMapInfo: props.layeringMapInfo,
  });

  const buttonProps = useMemo(() => ({
    onClick: handleGenerateIntersectsAndSubmit,
    text: t('Layer Map Now'),
    isDisabled: isLoading || !areAllRowsSelected,
  }), [areAllRowsSelected, handleGenerateIntersectsAndSubmit, isLoading, t]);

  const onUpdateBaseScrollState = useCallback(() => {
    const scrollTop = getScrollableElementFromScrollbarRef(baseScrollbarRef.current)?.scrollTop;
    const theOtherScrollElement = getScrollableElementFromScrollbarRef(columnNamesScrollbarRef.current);
    if (theOtherScrollElement && (scrollTop !== undefined)) {
      theOtherScrollElement.scrollTo({ top: scrollTop, behavior: 'instant' });
    }
  }, [baseScrollbarRef, columnNamesScrollbarRef]);

  const onUpdateColumnNamesScrollState = useCallback(() => {
    const scrollTop = getScrollableElementFromScrollbarRef(columnNamesScrollbarRef.current)?.scrollTop;
    const theOtherScrollElement = getScrollableElementFromScrollbarRef(baseScrollbarRef.current);
    if (theOtherScrollElement && (scrollTop !== undefined)) {
      theOtherScrollElement.scrollTo({ top: scrollTop, behavior: 'instant' });
    }
  }, [baseScrollbarRef, columnNamesScrollbarRef]);

  const getInvokeCallbackAndScroll = useCallback((callback: () => void) => () => {
    callback();
    delay(SCROLL_DELAY).then(() => {
      const columnNamesElement = getScrollableElementFromScrollbarRef(columnNamesScrollbarRef.current);
      scrollToBottom(columnNamesElement);
    });
  }, [columnNamesScrollbarRef]);

  const scrollToRowIndex = useCallback((rowIndex: number) => {
    const rowNode = columnNamesContainerRef.current?.querySelector(`[data-layered-map-row-index="${rowIndex}"]`);

    if (!rowNode || !getScrollableElementFromScrollbarRef(baseScrollbarRef.current)) {
      return;
    }

    rowNode.scrollIntoView();
  }, [baseScrollbarRef]);

  const scrollToFirstEmptyRow = useCallback(() => {
    if (notNull(firstEmptyRowIndex)) {
      scrollToRowIndex(firstEmptyRowIndex);
    }
  }, [firstEmptyRowIndex, scrollToRowIndex]);

  const scrollToFirstRowWithNewColumn = useCallback(() => {
    if (notNull(firstNewRowIndex)) {
      scrollToRowIndex(firstNewRowIndex);
    }
  }, [firstNewRowIndex, scrollToRowIndex]);

  return (
    <LayeredMapSectionHolderComponent
      buttonProps={buttonProps}
      maxContentWidth="100%"
      additionalButtons={[
        ...(!isLoading && notNull(firstNewRowIndex)) ? [(
          <button
            key="newRows"
            css={[warningButtonStyle, newColumnsButtonStyle]}
            onClick={scrollToFirstRowWithNewColumn}
          >
            <FontAwesomeIcon css={warningColumnsButtonIconStyle} icon={faCircleInfo} />
            <span css={warningButtonTextStyle}>
              {t('layering.matching.newColumnsAutomatched')}
            </span>
          </button>
        )] : [],
        ...(!isLoading && !areAllRowsSelected) ? [(
          <button
            key="unnamedColumns"
            css={[warningButtonStyle, unnamedColumnsButtonStyle]}
            onClick={scrollToFirstEmptyRow}
          >
            <FontAwesomeIcon css={warningColumnsButtonIconStyle} icon={faExclamationTriangle} />
            <span css={warningButtonTextStyle}>
              {t('Unnamed columns detected')}
            </span>
          </button>
        )] : [],
      ]}
    >
      <div css={helpfulTipStyle}>
        {t('Align each source’s fields into one output column.')}
        <HintTooltipComponent
          size={24}
          tooltipContent={(
            <Trans
              i18nKey="LayeredMapMatchupTip"
              components={helpfulTipComponents}
            />
          )}
        />
      </div>
      <div css={containerStyle}>
        <div css={baseMapsScrollbarStyle}>
          <div css={baseMapWrapperStyle}>
            <OverlayScrollbarComponent
              type={ScrollbarType.Horizontal}
            >
              <div css={horizontalScrollbarProperties}>
                <div css={baseMapsNamesStyle}>
                  {props.maps.map(map => (
                    <div
                      css={ellipsisStyle}
                      key={map.id}
                    >
                      {map.name}
                    </div>
                  ))}
                </div>
                <OverlayScrollbarComponent
                  ref={baseScrollbarRef}
                  isScrollbarHidden
                  maxHeight={MAX_VERTICAL_HEIGHT}
                  onUpdate={onUpdateBaseScrollState}
                  type={ScrollbarType.Vertical}
                >
                  <div css={baseMapsWrapperStyle}>
                    {rows.map((row, index) => (
                      <div
                        css={rowStyle}
                        key={index}
                      >
                        {props.maps.map(map => (
                          <RegularDropdownComponent
                            css={baseMapColumnStyle}
                            triggerStyle={
                              row?.selectedSourceColumnPerMap?.[map.id] ? undefined : unMatchedDropDownTriggerStyle
                            }
                            inputSize={DROPDOWN_TRIGGER_SIZE}
                            isDisabled={isLoading || map.isDisabled}
                            inPortal
                            key={map.id}
                            onChange={(value) => onSourceColumnChange(value ?? null, map.id, index)}
                            options={sourceColumnsPerMap[map.id] || []}
                            placeholder={t('None')}
                            value={row?.selectedSourceColumnPerMap?.[map.id]}
                            compareOptions={sourceColumnComparator}
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                </OverlayScrollbarComponent>
              </div>
            </OverlayScrollbarComponent>
          </div>
        </div>

        <div css={combinedSectionStyle}>
          <div css={combinedColumnNameStyle}>
            <LimitNumberOfLinesComponent maxNumberOfLines={2} >
              {props.mapName}
            </LimitNumberOfLinesComponent>
          </div>
          <OverlayScrollbarComponent
            ref={columnNamesScrollbarRef}
            maxHeight={MAX_VERTICAL_HEIGHT}
            onUpdate={onUpdateColumnNamesScrollState}
            type={ScrollbarType.Vertical}
            css={{ maxHeight: MAX_VERTICAL_HEIGHT, minHeight: 0 }}
          >
            <div
              ref={columnNamesContainerRef}
              css={combinedColumnsStyle({ isPadded: rows.length > MAX_ITEMS_IN_COLUMN })}
            >
              {rows.map((row, index) => (
                <div
                  key={index}
                  css={rowStyle}
                  data-layered-map-row-index={index}
                >
                  {row.isRemovable && !disableColumnNamesEditing && (
                    <>
                      <RoundButtonComponent
                        icon={faListTree}
                        size={RoundButtonSize.Small}
                        buttonStyle={row.onlyOneSourceMap ? RoundButtonStyle.Disabled : RoundButtonStyle.Secondary}
                        isDisabled={row.onlyOneSourceMap}
                        onClick={row.onlyOneSourceMap ?
                          undefined :
                          getInvokeCallbackAndScroll(() => onRemoveRow(index))}
                        tooltipDelayShow={500}
                        tooltipLabel={row.onlyOneSourceMap ?
                          t('This column association cannot be broken up.') :
                          t('Break up this column matching')}
                      />
                      <RoundButtonComponent
                        icon={faCircleXmark}
                        size={RoundButtonSize.Small}
                        buttonStyle={row.isIncluded ? RoundButtonStyle.Primary : RoundButtonStyle.DangerInverted}
                        onClick={() => toggleRowInclusion(index)}
                        tooltipDelayShow={500}
                        tooltipLabel={row.isIncluded ?
                          t('These columns will be included in the layered map') :
                          t('These columns will be excluded from the layered map')}
                      />
                    </>
                  )}
                  <div css={combinedDropdownStyle} >
                    <SectionAutocompleteComponent
                      isLoading={isLoading}
                      onChange={(value) => onTargetColumnNameChange(value, index)}
                      value={row.targetColumnName}
                      options={row.availableTargetColumnNames.map(columnName => ({
                        name: columnName.name,
                        value: columnName.value || columnName.name,
                      }))}
                      isDisabled={disableColumnNamesEditing || !row.isIncluded}
                    />
                  </div>
                </div>
              ))}
            </div>
          </OverlayScrollbarComponent>
          <div css={addMoreWrapperStyle}>
            <AddMorePlaceholderComponent
              onClick={getInvokeCallbackAndScroll(onAddRow)}
              title={t('Select More Columns')}
            />
          </div>
        </div>
      </div>
    </LayeredMapSectionHolderComponent>
  );
};
